import * as React from 'react';

import { CHUNK_RELOAD_STORAGE_KEY, CHUNK_RELOAD_WINDOW_MS } from 'domain/consts/chunk-reload';

interface Deps {
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined;
  reload: () => void;
  now: () => number;
}

const CHUNK_ERROR =
  /Loading (CSS )?chunk|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

const isChunkError = (e: unknown) =>
  e instanceof Error && (e.name === 'ChunkLoadError' || CHUNK_ERROR.test(e.message));

// Reading sessionStorage throws when site data is blocked.
const sessionStorageOrUndefined = () => {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

const defaultDeps = (): Deps => ({
  storage: sessionStorageOrUndefined(),
  reload: () => window.location.reload(),
  now: () => Date.now()
});

// At most one reload per window, and never reset on success: a lazy layout that loads after the
// reload must not re-arm it for a child chunk that is still broken. No storage means no reload.
const claimReload = ({ storage, now }: Deps) => {
  try {
    if (!storage) return false;
    const last = Number(storage.getItem(CHUNK_RELOAD_STORAGE_KEY));
    const elapsed = now() - last;
    if (last && elapsed >= 0 && elapsed < CHUNK_RELOAD_WINDOW_MS) return false;
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now()));
    return true;
  } catch {
    return false;
  }
};

export const loadWithRetry = async <T>(load: () => Promise<T>, overrides?: Partial<Deps>): Promise<T> => {
  const deps = { ...defaultDeps(), ...overrides };
  try {
    return await load();
  } catch (first) {
    if (!isChunkError(first)) throw first;
    // Covers a dropped request; a chunk missing after a deploy fails again and falls through to the reload.
    try {
      return await load();
    } catch (second) {
      if (!isChunkError(second) || !claimReload(deps)) throw second;
      deps.reload();
      // Keep Suspense pending while the page reloads.
      return new Promise<T>(() => undefined);
    }
  }
};

export const lazyWithRetry: typeof React.lazy = (factory) => React.lazy(() => loadWithRetry(factory));
